/*
 * 戒导航 · 网站导航模块（jiedh/nav）前台 JS 总出口
 *
 * 运行范围：
 *   仅在 Flarum 的 SPA 页面（论坛 /all、博客 /blog 等）加载；
 *   网站首页（导航站，路由 /nav 或被 default_route 复用为 /）是服务端直接渲染的
 *   自定义 HTML（见 packages/nav/views/nav.php），不会加载本文件。
 *
 * 职责（对应 doc/功能问题排查记录.md）：
 *   FQ-003 修正顶栏站名「戒导航-jieDH.com」的点击行为——点击后整页回到网站首页；
 *   FQ-004 顶栏新增「首页」入口（v1.21 起带小房子图标），一键回到网站首页；
 *   FQ-027 顶栏新增「论坛」「博客」模块入口（v1.22，参考 flarum 官网 discuss.flarum.org
 *          顶栏菜单设计），论坛/博客之间一键互跳，当前所在模块自动高亮。
 *   FQ-028 顶栏新增「知识库」入口（decision 7，见 doc/知识库与文件下载中心设计方案.md §十）：
 *          知识库 / 下载中心入口与「论坛」「博客」同级，统一放头部主导航；
 *          知识库入口以 linkrobins/wiki 注册的 'linkrobins-wiki.index' 路由表为准，扩展停用即自动隐藏。
 *   FQ-028 顶栏新增「下载中心」入口（同 decision 7，P5a；2026-10-10 随「P4 改造」升级）：
 *          下载中心 /downloads 已由「服务端渲染独立页」改为「渲染在论坛 SPA 外壳内」
 *          （与 packages/checkin 戒打卡一致），页面路由已进入前端 app.routes，
 *          故入口由原生 <a> 整页跳转升级为官方 LinkButton + app.route('downloads') 走 SPA 路由，
 *          并做 active 高亮；详见 addDownloadsNavButton() 注释。
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import Icon from 'flarum/common/components/Icon';
import LinkButton from 'flarum/common/components/LinkButton';

/**
 * 网站首页地址（即站点根地址，由后台 default_route 渲染为戒导航导航站）。
 */
function siteHomeUrl() {
  return app.forum.attribute('baseUrl') || '/';
}

/**
 * FQ-003：修正顶栏站名 #home-link 的跳转。
 *
 * 问题现象：在论坛 / 博客页点击顶栏站名，只会回到「当前模块的首页」，回不到网站首页。
 * 根本原因：Flarum 核心在 ForumApplication::mount() 里给 #home-link 绑定了点击监听：
 *     e.preventDefault(); app.history.home();
 * 它只做 SPA 内部路由（不真正请求服务端根地址 /），因此不会渲染服务端的导航站首页。
 *
 * 修复方式：在「捕获阶段」监听 document。
 *   - 捕获阶段早于核心绑在元素上的冒泡监听执行；一旦调用 stopPropagation()，
 *     事件就不会继续传播到 #home-link 本身，核心的 SPA 拦截随即失效；
 *   - 不调用 preventDefault()，保留浏览器 <a href> 的默认行为，
 *     于是会整页请求站点根地址，真正回到网站首页。
 *
 * 带 Ctrl / Cmd / Shift / Alt 或非左键点击时直接放行，
 * 让浏览器继续负责「新标签页打开 / 下载 / 菜单」等原生行为。
 */
function fixHomeLink() {
  document.addEventListener('click', event => {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    if (!target?.closest?.('#home-link')) return;

    // 阻断核心的 SPA 监听；默认跳转仍然生效，实现整页回到网站首页。
    event.stopPropagation();
  }, true);
}

/**
 * FQ-004：顶栏新增「首页」入口。
 *
 * 使用普通 <a> 而非 LinkButton：
 *   LinkButton 走的是 Mithril 的 m.route.Link，属于 SPA 内部路由，
 *   无法真正跳回「服务端渲染」的网站首页，因此这里用原生链接。
 *   原生 <a> 也不会被核心的 routeInternalLinks 拦截（它只处理 a.UrlLink--internal），
 *   点击即整页跳转到网站首页，与站名的行为保持一致。
 *
 * 样式沿用 Flarum 顶栏文字链接的官方写法 `Button Button--link`
 * （见 core/js/src/forum/components/HeaderSecondary.js），与主站风格保持一致。
 *
 * v1.21 变更：
 *   ① 文案由「网站首页」改为「首页」（语言包 jiedh-nav.forum.home）；
 *   ② 前置小房子图标（FontAwesome `fas fa-house`），让「点击回首页」的语义
 *      无需读文字即可直观识别。
 *   ③ 结构对齐核心 Button 组件的 DOM：Icon(.Button-icon) → .Button-label > .Button-labelText。
 *      这样可直接复用核心 less 的排版（.Button 自带 inline-flex + gap:7px 负责图标与文字间距，
 *      .Button .Button-icon{display:inline-block} 负责不被 FA 的 display:block 破坏，
 *      .Button-label{min-width:0} 负责窄屏下的省略号收敛）。
 *      图标名称走 Icon 组件，会经过管理后台「强制图标样式」设置（fontAwesomeForcedStyle）处理。
 */
function addWebsiteHomeButton() {
  extend(HeaderPrimary.prototype, 'items', function (items) {
    items.add('jiedh-nav-website-home', m('a', {
      className: 'Button Button--link',
      href: siteHomeUrl()
    },
    // 小房子图标：语义即「回到首页」，与文案形成双重提示
    m(Icon, {
      name: 'fas fa-house',
      className: 'Button-icon'
    }),
    // 文案容器：结构与核心 Button 一致，保证窄屏下可正常省略
    m('span', {
      className: 'Button-label'
    }, m('span', {
      className: 'Button-labelText'
    }, app.translator.trans('jiedh-nav.forum.home')))), 100);
  });
}

/**
 * 当前 SPA 路由名（核心由 app.history.push / 路由解析器写入 RouteState，
 * 参考 core/js/src/forum/states/GlobalSearchState.ts 的 app.current.get('routeName') 用法）。
 */
function currentRouteName() {
  return String(app.current.get('routeName') ?? '');
}

/**
 * FQ-027（v1.22）：顶栏新增「论坛」「博客」模块入口，参考 flarum 官网
 * discuss.flarum.org 顶栏菜单（Home/Docs/Donate，图标+文字）的设计。
 *
 * 技术要点：
 *   ① 扩展点与「首页」按钮相同：extend(HeaderPrimary.prototype, 'items')，
 *      窄屏下由核心的 OverflowingList 自动收进「···」溢出菜单，无需额外适配；
 *   ② 用官方 LinkButton 组件（Button 子类 + m.route.Link），
 *      走 SPA 内部路由、点击无整页刷新；`force` 默认 true 只是mithril key 强制重渲染，
 *      与 fof/blog 侧栏「博客」链接（addSidebarNav.tsx）同款用法；
 *   ③ 图标：论坛 `fas fa-comments`（对话气泡）、博客 `fas fa-pen-nib`（钢笔尖），
 *      与首页的 `fas fa-house` 同为 fas 风格；
 *   ④ 当前模块高亮：Flarum 2.x 的 LinkButton `active` 只写 DOM 属性、不产生类名
 *      （见其 view() 的 String(attrs.active) 实现），因此这里显式拼进 `active` 类，
 *      命中核心 less 的 `.Button--link.active { color: var(--link-color) }`；
 *   ⑤ 降级：博客入口以客户端路由表里是否注册了 `blog` 路由为准
 *      （'blog' in app.routes），将来若停用 fof/blog 扩展，此入口自动消失、不报错；
 *      判断放在 items 回调内（渲染期执行），避免初始化器执行顺序导致误判；
 *   ⑥ 注意 2.x 的 Button 无默认类名（见其文档注释），必须手写
 *      'Button Button--link' 才能套用官方按钮样式。
 */
function addModuleNavButtons() {
  extend(HeaderPrimary.prototype, 'items', function (items) {
    const routeName = currentRouteName();

    // 「论坛」入口 → 讨论列表 app.route('index')（即 /all，核心 routes.ts）。
    // 高亮口径：仅论坛的浏览/阅读类页面（列表 index、帖子 discussion、标签 tags）视为「在论坛中」；
    // 账户设置、用户主页等页面两个模块都不高亮，避免误导。
    const forumRoutes = ['index', 'discussion', 'tags'];
    items.add('jiedh-nav-forum', m(LinkButton, {
      className: 'Button Button--link' + (forumRoutes.indexOf(routeName) !== -1 ? ' active' : ''),
      icon: 'fas fa-comments',
      href: app.route('index')
    }, app.translator.trans('jiedh-nav.forum.forum')), 90);

    // 「博客」入口 → 博客列表 app.route('blog')（即 /blog，由 fof/blog 注册）。
    // blogCategory / blogArticle / blogComposer 等子路由名均以 blog 开头，统一高亮。
    const inBlog = routeName.indexOf('blog') === 0;
    if ('blog' in app.routes) {
      items.add('jiedh-nav-blog', m(LinkButton, {
        className: 'Button Button--link' + (inBlog ? ' active' : ''),
        icon: 'fas fa-pen-nib',
        href: app.route('blog')
      }, app.translator.trans('jiedh-nav.forum.blog')), 80);
    }

    // 「知识库」入口 → 知识库首页 app.route('linkrobins-wiki.index')（即 /wiki，由 linkrobins/wiki 注册）。
    // 归属：决策 7（2026-10-10）——知识库/下载中心入口与「论坛」「博客」同级，统一放头部主导航。
    // 高亮口径：linkrobins-wiki.compose / .show / .edit 等子路由名均以 'linkrobins-wiki' 开头，统一高亮。
    // 降级：以路由表里是否注册了 'linkrobins-wiki.index' 为准（同「博客」的 'blog' in app.routes 写法），
    //       将来若停用 linkrobins/wiki 扩展，此入口自动消失、不报错、不留死链。
    // 图标 fas fa-book：与 LR Wiki 扩展自身后台图标一致，风格延续 fas 系列。
    const inWiki = routeName.indexOf('linkrobins-wiki') === 0;
    if ('linkrobins-wiki.index' in app.routes) {
      items.add('jiedh-nav-wiki', m(LinkButton, {
        className: 'Button Button--link' + (inWiki ? ' active' : ''),
        icon: 'fas fa-book',
        href: app.route('linkrobins-wiki.index')
      }, app.translator.trans('jiedh-nav.forum.wiki')), 75);
    }
  });
}

/**
 * FQ-028（决策 7）：顶栏新增「下载中心」入口。
 *
 * 归属：与「论坛」「博客」「知识库」同级，统一放头部主导航（决策 ⑦）。
 *
 * v1.23 变更（2026-10-10，随「P4 改造」升级）：
 *   下载中心 /downloads 已由「服务端渲染独立页」改为「渲染在论坛 SPA 外壳内」
 *   （与 packages/checkin 戒打卡一致）：后端 `Extend\Frontend('forum')->route()`
 *   放行 URL，前端 JS 侧 `new Extend.Routes().add('downloads', '/downloads', ...)`
 *   注册 Mithril 组件（见 packages/downloads/js/src/forum/extend.ts）。
 *   因此页面路由**已进入**前端 `app.routes`（`app.routes` 只收 JS 侧
 *   `Extend\Routes` 注册的路由，见 core/js/src/common/extenders/Routes.ts 的
 *   `Object.assign(app.routes, this.routes)`），可以调用 `app.route('downloads')`。
 *   于是本入口由原生 `<a>` 整页跳转升级为官方 LinkButton，与上面三个模块入口实现统一。
 *
 * 技术要点（与「论坛」「博客」「知识库」入口完全同款）：
 *   ① 用官方 LinkButton 组件（Button 子类 + m.route.Link），走 SPA 内部路由、点击无整页刷新；
 *   ② 当前模块高亮：LinkButton `active` 只写 DOM 属性、不产生类名
 *      （见其 view() 的 String(attrs.active) 实现），因此这里显式拼进 `active` 类，
 *      命中核心 less 的 `.Button--link.active { color: var(--link-color) }`；
 *   ③ 高亮口径：downloads.category / downloads.show 等子路由名均以 'downloads' 开头，统一高亮；
 *   ④ 降级：以客户端路由表里是否注册了 `downloads` 路由为准（'downloads' in app.routes），
 *      将来若停用 jiedh/downloads 扩展，此入口自动消失、不报错、不留死链；
 *      判断放在 items 回调内（渲染期执行），避免初始化器执行顺序导致误判；
 *      注意 2.x 的 Button 无默认类名，必须手写 'Button Button--link' 才能套用官方按钮样式；
 *   ⑤ 图标 fas fa-download（下载箭头），与首页/论坛/博客/知识库同为 fas 风格。
 */
function addDownloadsNavButton() {
  extend(HeaderPrimary.prototype, 'items', function (items) {
    // 降级守卫：下载中心扩展停用时，JS 路由不存在，直接不渲染入口（避免 app.route 抛错 / 死链）
    if (!('downloads' in app.routes)) return;

    // 高亮判定：index(/downloads)、category(/downloads/c/:slug)、show(/downloads/r/:slug)
    // 路由名均以 'downloads' 开头，统一视为「在下载中心」。
    const inDownloads = currentRouteName().indexOf('downloads') === 0;
    items.add('jiedh-nav-downloads', m(LinkButton, {
      className: 'Button Button--link' + (inDownloads ? ' active' : ''),
      icon: 'fas fa-download',
      href: app.route('downloads')
    }, app.translator.trans('jiedh-nav.forum.downloads')),
    // 70：位于「知识库」(75) 之后，保持 首页100 > 论坛90 > 博客80 > 知识库75 > 下载中心70 的顺序
    70);
  });
}
app.initializers.add('jiedh-nav', () => {
  fixHomeLink();
  addWebsiteHomeButton();
  addModuleNavButtons();
  addDownloadsNavButton();
});